/**
 * Form Handler Create / Edit
 * Configure a form handler endpoint with field mappings and completion actions.
 */

import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useAuth } from "../contexts/AuthContext";
import {
  getFormHandler,
  createFormHandler,
  updateFormHandler,
  getFormHandlerEndpointUrl,
  getFormHandlerDirectUrl,
  validateLookupMappings,
  type LookupMappingVerdict,
} from "../services/formHandlers.service";
import { getLists } from "../services/lists.service";
import { getAttributeDefinitions } from "../services/contactAttributes.service";
import { supabase } from "../services/supabase";
import LoadingSpinner from "../components/common/LoadingSpinner";
import {
  ArrowLeftIcon,
  PlusIcon,
  TrashIcon,
  ClipboardDocumentIcon,
} from "@heroicons/react/24/outline";
import {
  ALLOWED_CONTACT_FIELDS,
  ALLOWED_CONTACT_FIELD_LABELS,
  DEFAULT_FIELD_MAPPINGS,
  FORM_HANDLER_ALLOWED_SOURCE_TYPES,
  type FieldMapping,
  type CompletionActions,
  type MergeFieldMapping,
} from "../types/formHandler.types";
import MergeFieldMapper from "../components/campaigns/MergeFieldMapper";

// Custom attribute targets are encoded as `custom:<slug>` in the
// contact_field column of a field mapping. The edge function partitions
// these out at write time and routes them through the
// upsert_contact_attribute_value RPC instead of the built-in contact
// columns, so this prefix must stay in sync between frontend and backend.
const CUSTOM_ATTRIBUTE_PREFIX = "custom:";

// Mirrors SEQUENCE_ENROLL_CAP in handle-form-submission/index.ts. A saved
// config above the cap fails the edge's runtime validation, which skips ALL
// processing for every submission — so refuse it at save time too.
const SEQUENCE_ENROLL_CAP = 10;

function verdictReasonLabel(
  reason: LookupMappingVerdict["reason"] | undefined,
): string {
  switch (reason) {
    case "template_field_missing":
      return "variable name is empty";
    case "lookup_id_missing":
      return "no lookup table selected";
    case "lookup_not_found":
      return "lookup table no longer exists";
    case "value_column_missing":
      return "value column is missing from the lookup";
    case "default_row_missing":
      return "static content bundle has no starred default row";
    case "invalid_resolution_mode":
      return "lookup resolution mode is invalid";
    case "unsupported_source_type":
      return "source type is not supported here";
    default:
      return "validation failed";
  }
}

export default function FormHandlerEdit() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const isNew = !id;

  // Form state
  const [name, setName] = useState("");
  const [successUrl, setSuccessUrl] = useState("");
  const [fieldMappings, setFieldMappings] = useState<FieldMapping[]>(
    DEFAULT_FIELD_MAPPINGS,
  );
  const [completionActions, setCompletionActions] = useState<CompletionActions>(
    {
      autoresponder: { enabled: false, template_id: "" },
      add_to_lists: { enabled: false, list_ids: [] },
      add_tags: { enabled: false, tags: [] },
      notify_users: { enabled: false, profile_ids: [] },
      enroll_in_sequences: { enabled: false, sequence_ids: [] },
    },
  );

  // UI state
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMsg, setSuccessMsg] = useState<string | null>(null);
  const [copiedUrl, setCopiedUrl] = useState<"primary" | "direct" | null>(null);
  const [newTag, setNewTag] = useState("");
  const [savedId, setSavedId] = useState<string | null>(id || null);
  const [lookupVerdicts, setLookupVerdicts] = useState<LookupMappingVerdict[]>(
    [],
  );

  // Load existing handler
  const { data: handler, isLoading } = useQuery({
    queryKey: ["form-handler", id],
    queryFn: () => getFormHandler(id!),
    enabled: !!id,
  });

  // Load lists for completion actions
  const { data: lists } = useQuery({
    queryKey: ["lists"],
    queryFn: () => getLists(),
  });

  // Load profiles for notify_users
  const { data: profiles } = useQuery({
    queryKey: ["profiles-active"],
    queryFn: async () => {
      const { data } = await supabase
        .from("profiles")
        .select("id, first_name, last_name, email")
        .neq("role", "readonly") // a read-only viewer is never a notification recipient (owner 2026-11-03)
        .order("first_name");
      return data || [];
    },
  });

  // Load ACTIVE sequences for the enroll_in_sequences picker. Only active
  // sequences are offered (the edge function also re-checks at run time —
  // a sequence paused after save is skipped, not errored).
  const { data: activeSequences } = useQuery({
    queryKey: ["sequences-active-picker"],
    queryFn: async () => {
      const { data } = await supabase
        .from("message_sequences")
        .select("id, name, status")
        .eq("status", "active")
        .order("name")
        .limit(500);
      return data || [];
    },
  });

  // Load custom attribute definitions for the CRM Field dropdown.
  // Form handlers create new contacts as Prospect, so we surface attributes
  // whose applicable_to='all' or whose applicable_contact_types explicitly
  // include 'Prospect'. This is dropdown UX only — the edge function does
  // not enforce applicability server-side, so existing non-Prospect
  // contacts can still receive an attribute if they submit the form.
  const { data: attributeDefinitions } = useQuery({
    queryKey: ["contact-attribute-definitions-active"],
    queryFn: async () => {
      const all = await getAttributeDefinitions();
      return all
        .filter((d) => d.is_active)
        .filter((d) => {
          if (d.applicable_to === "all") {
            return true;
          }
          const types = d.applicable_contact_types;
          return Array.isArray(types) && types.includes("Prospect");
        });
    },
  });

  const autoresponderContactFields = [
    ...ALLOWED_CONTACT_FIELDS.map((field) => ({
      value: field,
      label: ALLOWED_CONTACT_FIELD_LABELS[field] || field,
    })),
    ...((attributeDefinitions || []).map((definition) => ({
      value: `${CUSTOM_ATTRIBUTE_PREFIX}${definition.slug}`,
      label: `Attribute: ${definition.name}`,
    })) || []),
  ];

  // Populate form when handler loads
  useEffect(() => {
    if (handler) {
      setName(handler.name);
      setSuccessUrl(handler.success_url);
      setFieldMappings(
        handler.field_mappings?.length > 0
          ? handler.field_mappings
          : DEFAULT_FIELD_MAPPINGS,
      );
      setCompletionActions({
        autoresponder: {
          enabled: false,
          template_id: "",
          ...handler.completion_actions?.autoresponder,
        },
        add_to_lists: {
          enabled: false,
          list_ids: [],
          ...handler.completion_actions?.add_to_lists,
        },
        add_tags: {
          enabled: false,
          tags: [],
          ...handler.completion_actions?.add_tags,
        },
        notify_users: {
          enabled: false,
          profile_ids: [],
          ...handler.completion_actions?.notify_users,
        },
        enroll_in_sequences: {
          enabled: false,
          sequence_ids: [],
          ...handler.completion_actions?.enroll_in_sequences,
        },
      });
    }
  }, [handler]);

  const handleSave = async () => {
    setError(null);
    setSuccessMsg(null);

    if (!name.trim()) {
      setError("Name is required.");
      return;
    }
    if (!successUrl.trim()) {
      setError("Success URL is required.");
      return;
    }
    if (!successUrl.startsWith("https://")) {
      setError("Success URL must start with https://");
      return;
    }

    const emailMapping = fieldMappings.find((m) => m.contact_field === "email");
    if (!emailMapping) {
      setError("Field mappings must include an email mapping.");
      return;
    }

    // A handler whose stored config fails the edge function's runtime
    // validation skips ALL processing for every submission (logged as a
    // config error) — so refuse to save an enabled-but-empty enroll block.
    if (
      completionActions.enroll_in_sequences?.enabled &&
      (completionActions.enroll_in_sequences.sequence_ids || []).length === 0
    ) {
      setError(
        "Enroll in Sequence(s) is enabled but no sequence is selected. Select at least one sequence or turn the toggle off.",
      );
      return;
    }
    if (
      completionActions.enroll_in_sequences?.enabled &&
      (completionActions.enroll_in_sequences.sequence_ids || []).length >
        SEQUENCE_ENROLL_CAP
    ) {
      setError(
        `Enroll in Sequence(s) supports at most ${SEQUENCE_ENROLL_CAP} sequences. Deselect down to ${SEQUENCE_ENROLL_CAP} or fewer.`,
      );
      return;
    }

    const knownCustomSlugs = new Set(
      (attributeDefinitions || []).map((d) => d.slug),
    );
    const invalidMapping = fieldMappings.find((m) => {
      if (!m.form_field.trim()) {
        return true;
      }
      if (m.contact_field.startsWith(CUSTOM_ATTRIBUTE_PREFIX)) {
        const slug = m.contact_field.slice(CUSTOM_ATTRIBUTE_PREFIX.length);
        return !knownCustomSlugs.has(slug);
      }
      return !ALLOWED_CONTACT_FIELDS.includes(m.contact_field);
    });
    if (invalidMapping) {
      setError(`Invalid field mapping for "${invalidMapping.form_field}".`);
      return;
    }

    // Save-time drift check for autoresponder variable mappings.
    // Structural validation (regex, source-type enum, uniqueness) lives
    // on the edge function; this RPC only catches drift between mapping
    // references and the current campaign_lookups state.
    const variableMappings =
      completionActions.autoresponder?.variable_mappings ?? [];
    let verdicts: LookupMappingVerdict[] = [];
    if (
      completionActions.autoresponder?.enabled &&
      variableMappings.length > 0
    ) {
      try {
        verdicts = await validateLookupMappings(variableMappings);
      } catch (err: unknown) {
        setError(
          err instanceof Error
            ? `Variable validation failed: ${err.message}`
            : "Variable validation failed.",
        );
        return;
      }
      setLookupVerdicts(verdicts);
      if (verdicts.some((v) => !v.ok)) {
        setError(
          "One or more autoresponder variable mappings are invalid. Resolve the issues above before saving.",
        );
        return;
      }
    } else {
      setLookupVerdicts([]);
    }

    setSaving(true);
    try {
      const payload = {
        name: name.trim(),
        success_url: successUrl.trim(),
        field_mappings: fieldMappings.filter((m) => m.form_field.trim()),
        completion_actions: completionActions,
      };

      if (isNew) {
        const created = await createFormHandler(payload, user?.id);
        setSavedId(created.id);
        queryClient.invalidateQueries({ queryKey: ["form-handlers"] });
        setSuccessMsg("Handler created.");
        navigate(`/form-handlers/${created.id}/edit`, { replace: true });
      } else {
        await updateFormHandler(id!, payload);
        queryClient.invalidateQueries({ queryKey: ["form-handlers"] });
        queryClient.invalidateQueries({ queryKey: ["form-handler", id] });
        setSuccessMsg("Handler saved.");
      }
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : "Failed to save handler.");
    } finally {
      setSaving(false);
    }
  };

  const addFieldMapping = () => {
    setFieldMappings((prev) => [
      ...prev,
      { form_field: "", contact_field: "first_name" },
    ]);
  };

  const removeFieldMapping = (index: number) => {
    setFieldMappings((prev) => prev.filter((_, i) => i !== index));
  };

  const updateFieldMapping = (
    index: number,
    field: keyof FieldMapping,
    value: string,
  ) => {
    setFieldMappings((prev) =>
      prev.map((m, i) => (i === index ? { ...m, [field]: value } : m)),
    );
  };

  const updateAction = <K extends keyof CompletionActions>(
    key: K,
    updates: Partial<CompletionActions[K]>,
  ) => {
    setCompletionActions((prev) => ({
      ...prev,
      [key]: { ...prev[key], ...updates },
    }));
  };

  const addTag = () => {
    const trimmed = newTag.trim();
    if (!trimmed) {
      return;
    }
    const current = completionActions.add_tags?.tags || [];
    if (!current.includes(trimmed)) {
      updateAction("add_tags", { tags: [...current, trimmed] });
    }
    setNewTag("");
  };

  const removeTag = (tag: string) => {
    updateAction("add_tags", {
      tags: (completionActions.add_tags?.tags || []).filter((t) => t !== tag),
    });
  };

  const copyUrl = (type: "primary" | "direct") => {
    const url =
      type === "primary"
        ? getFormHandlerEndpointUrl(savedId!)
        : getFormHandlerDirectUrl(savedId!);
    navigator.clipboard.writeText(url);
    setCopiedUrl(type);
    setTimeout(() => setCopiedUrl(null), 2000);
  };

  if (!isNew && isLoading) {
    return (
      <div className="flex items-center justify-center h-96">
        <LoadingSpinner size="lg" text="Loading handler..." />
      </div>
    );
  }

  return (
    <div className="p-6 max-w-3xl mx-auto">
      {/* Header */}
      <div className="flex items-center gap-3 mb-6">
        <button
          onClick={() => navigate("/form-handlers")}
          className="p-2 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100"
        >
          <ArrowLeftIcon className="w-5 h-5" />
        </button>
        <h1 className="text-2xl font-bold text-gray-900">
          {isNew ? "New Form Handler" : "Edit Form Handler"}
        </h1>
      </div>

      {error && (
        <div className="mb-4 bg-red-50 border border-red-200 rounded-lg p-3 text-red-700 text-sm">
          {error}
        </div>
      )}
      {successMsg && (
        <div className="mb-4 bg-green-50 border border-green-200 rounded-lg p-3 text-green-700 text-sm">
          {successMsg}
        </div>
      )}

      <div className="space-y-6">
        {/* Section 1: Basic Settings */}
        <section className="bg-white border border-gray-200 rounded-xl p-5">
          <h2 className="text-base font-semibold text-gray-900 mb-4">
            Basic Settings
          </h2>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Handler Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="e.g. USLS Lead Form"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Success URL <span className="text-red-500">*</span>
              </label>
              <input
                type="url"
                value={successUrl}
                onChange={(e) => setSuccessUrl(e.target.value)}
                placeholder="https://uslawshield.com/thank-you"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
              <p className="mt-1 text-xs text-gray-500">
                Submitters are redirected here after the form is processed.
              </p>
            </div>
          </div>
        </section>

        {/* Section 2: Field Mappings */}
        <section className="bg-white border border-gray-200 rounded-xl p-5">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h2 className="text-base font-semibold text-gray-900">
                Field Mappings
              </h2>
              <p className="text-xs text-gray-500 mt-0.5">
                Map form field names to CRM contact fields.
              </p>
            </div>
            <button
              onClick={addFieldMapping}
              className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-medium text-blue-600 bg-blue-50 hover:bg-blue-100 rounded-lg"
            >
              <PlusIcon className="w-3.5 h-3.5" />
              Add Row
            </button>
          </div>

          <div className="space-y-2">
            <div className="grid grid-cols-2 gap-3 px-1 mb-1">
              <span className="text-xs font-medium text-gray-500 uppercase">
                Form Field Name
              </span>
              <span className="text-xs font-medium text-gray-500 uppercase">
                CRM Field
              </span>
            </div>
            {fieldMappings.map((mapping, index) => {
              const isEmail = mapping.contact_field === "email";
              return (
                <div
                  key={index}
                  className="grid grid-cols-2 gap-3 items-center"
                >
                  <input
                    type="text"
                    value={mapping.form_field}
                    onChange={(e) =>
                      updateFieldMapping(index, "form_field", e.target.value)
                    }
                    placeholder="form field name"
                    className="px-2.5 py-1.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                  <div className="flex items-center gap-2">
                    <select
                      value={mapping.contact_field}
                      onChange={(e) =>
                        updateFieldMapping(
                          index,
                          "contact_field",
                          e.target.value,
                        )
                      }
                      disabled={isEmail}
                      className="flex-1 px-2.5 py-1.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-50 disabled:text-gray-500"
                    >
                      <optgroup label="Standard fields">
                        {ALLOWED_CONTACT_FIELDS.map((f) => (
                          <option key={f} value={f}>
                            {ALLOWED_CONTACT_FIELD_LABELS[f] || f}
                          </option>
                        ))}
                      </optgroup>
                      {attributeDefinitions &&
                        attributeDefinitions.length > 0 && (
                          <optgroup label="Custom attributes">
                            {attributeDefinitions.map((d) => (
                              <option
                                key={`${CUSTOM_ATTRIBUTE_PREFIX}${d.slug}`}
                                value={`${CUSTOM_ATTRIBUTE_PREFIX}${d.slug}`}
                              >
                                {d.name}
                              </option>
                            ))}
                          </optgroup>
                        )}
                    </select>
                    {isEmail ? (
                      <span className="w-7 h-7" />
                    ) : (
                      <button
                        onClick={() => removeFieldMapping(index)}
                        className="p-1.5 text-gray-400 hover:text-red-600 rounded"
                      >
                        <TrashIcon className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </section>

        {/* Section 3: Completion Actions */}
        <section className="bg-white border border-gray-200 rounded-xl p-5">
          <h2 className="text-base font-semibold text-gray-900 mb-4">
            Completion Actions
          </h2>
          <div className="space-y-4">
            {/* Autoresponder */}
            <div className="border border-gray-200 rounded-lg p-4">
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-sm font-medium text-gray-900">
                  Send Autoresponder
                </h3>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    className="sr-only peer"
                    checked={completionActions.autoresponder?.enabled || false}
                    onChange={(e) =>
                      updateAction("autoresponder", {
                        enabled: e.target.checked,
                      })
                    }
                  />
                  <div className="w-9 h-5 bg-gray-200 rounded-full peer peer-checked:bg-blue-600 after:content-[''] after:absolute after:top-0.5 after:start-0.5 after:bg-white after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:after:translate-x-4" />
                </label>
              </div>
              {completionActions.autoresponder?.enabled && (
                <div className="space-y-4">
                  <div>
                    <label className="block text-xs text-gray-600 mb-1">
                      SendGrid Template ID
                    </label>
                    <input
                      type="text"
                      value={completionActions.autoresponder?.template_id || ""}
                      onChange={(e) =>
                        updateAction("autoresponder", {
                          template_id: e.target.value,
                        })
                      }
                      placeholder="d-abc123..."
                      className="w-full px-2.5 py-1.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                    <p className="mt-1 text-xs text-gray-500">
                      Sent from noreply@uslawshield.com. The template always
                      receives <code>first_name</code>, <code>last_name</code>,
                      and <code>email</code>. Additional variables can be mapped
                      below.
                    </p>
                  </div>

                  <div className="border-t border-gray-200 pt-4">
                    <div className="flex items-center justify-between mb-2">
                      <label className="block text-xs font-medium text-gray-700">
                        Template Variables
                      </label>
                    </div>
                    <p className="text-xs text-gray-500 mb-3">
                      Use <code>{`{{variable_name}}`}</code> in your SendGrid
                      template, or <code>{`{{{variable_name}}}`}</code> if the
                      value contains HTML.
                    </p>
                    <MergeFieldMapper
                      mergeFields={[]}
                      mappings={
                        completionActions.autoresponder?.variable_mappings ?? []
                      }
                      onChange={(next: MergeFieldMapping[]) =>
                        updateAction("autoresponder", {
                          variable_mappings: next,
                        })
                      }
                      includeVoucherFields={false}
                      allowedSourceTypes={FORM_HANDLER_ALLOWED_SOURCE_TYPES}
                      contactFields={autoresponderContactFields}
                    />

                    {lookupVerdicts.some((v) => !v.ok) && (
                      <div className="mt-3 bg-red-50 border border-red-200 rounded p-3">
                        <p className="text-xs font-medium text-red-800 mb-1">
                          Some variable mappings could not be validated:
                        </p>
                        <ul className="text-xs text-red-700 space-y-0.5">
                          {lookupVerdicts
                            .filter((v) => !v.ok)
                            .map((v, i) => (
                              <li key={`${v.templateField}-${i}`}>
                                <code>{v.templateField || "(unnamed)"}</code>:{" "}
                                {verdictReasonLabel(v.reason)}
                              </li>
                            ))}
                        </ul>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>

            {/* Add to Lists */}
            <div className="border border-gray-200 rounded-lg p-4">
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-sm font-medium text-gray-900">
                  Add to List(s)
                </h3>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    className="sr-only peer"
                    checked={completionActions.add_to_lists?.enabled || false}
                    onChange={(e) =>
                      updateAction("add_to_lists", {
                        enabled: e.target.checked,
                      })
                    }
                  />
                  <div className="w-9 h-5 bg-gray-200 rounded-full peer peer-checked:bg-blue-600 after:content-[''] after:absolute after:top-0.5 after:start-0.5 after:bg-white after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:after:translate-x-4" />
                </label>
              </div>
              {completionActions.add_to_lists?.enabled && (
                <div>
                  <label className="block text-xs text-gray-600 mb-1">
                    Select Lists
                  </label>
                  <select
                    multiple
                    value={completionActions.add_to_lists?.list_ids || []}
                    onChange={(e) => {
                      const selected = Array.from(e.target.selectedOptions).map(
                        (o) => o.value,
                      );
                      updateAction("add_to_lists", { list_ids: selected });
                    }}
                    className="w-full px-2.5 py-1.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 min-h-[100px]"
                  >
                    {(lists || [])
                      .filter((list) => list.is_dynamic === false)
                      .map((list) => (
                        <option key={list.id} value={list.id}>
                          {list.name}
                        </option>
                      ))}
                  </select>
                  <p className="mt-1 text-xs text-gray-500">
                    Hold Cmd/Ctrl to select multiple. Dynamic (rule-based) lists
                    are not shown — they populate from criteria, not from
                    form-handler additions.
                  </p>
                </div>
              )}
            </div>

            {/* Add Tags */}
            <div className="border border-gray-200 rounded-lg p-4">
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-sm font-medium text-gray-900">Add Tags</h3>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    className="sr-only peer"
                    checked={completionActions.add_tags?.enabled || false}
                    onChange={(e) =>
                      updateAction("add_tags", { enabled: e.target.checked })
                    }
                  />
                  <div className="w-9 h-5 bg-gray-200 rounded-full peer peer-checked:bg-blue-600 after:content-[''] after:absolute after:top-0.5 after:start-0.5 after:bg-white after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:after:translate-x-4" />
                </label>
              </div>
              {completionActions.add_tags?.enabled && (
                <div>
                  <div className="flex gap-2 mb-2">
                    <input
                      type="text"
                      value={newTag}
                      onChange={(e) => setNewTag(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") {
                          e.preventDefault();
                          addTag();
                        }
                      }}
                      placeholder="Add a tag..."
                      className="flex-1 px-2.5 py-1.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                    <button
                      onClick={addTag}
                      className="px-3 py-1.5 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg text-sm"
                    >
                      Add
                    </button>
                  </div>
                  <div className="flex flex-wrap gap-1.5">
                    {(completionActions.add_tags?.tags || []).map((tag) => (
                      <span
                        key={tag}
                        className="inline-flex items-center gap-1 px-2 py-0.5 bg-blue-100 text-blue-800 rounded-full text-xs"
                      >
                        {tag}
                        <button
                          onClick={() => removeTag(tag)}
                          className="text-blue-600 hover:text-blue-900"
                        >
                          ×
                        </button>
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Notify Users */}
            <div className="border border-gray-200 rounded-lg p-4">
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-sm font-medium text-gray-900">
                  Notify CRM User(s)
                </h3>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    className="sr-only peer"
                    checked={completionActions.notify_users?.enabled || false}
                    onChange={(e) =>
                      updateAction("notify_users", {
                        enabled: e.target.checked,
                      })
                    }
                  />
                  <div className="w-9 h-5 bg-gray-200 rounded-full peer peer-checked:bg-blue-600 after:content-[''] after:absolute after:top-0.5 after:start-0.5 after:bg-white after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:after:translate-x-4" />
                </label>
              </div>
              {completionActions.notify_users?.enabled && (
                <div>
                  <label className="block text-xs text-gray-600 mb-1">
                    Select Users
                  </label>
                  <select
                    multiple
                    value={completionActions.notify_users?.profile_ids || []}
                    onChange={(e) => {
                      const selected = Array.from(e.target.selectedOptions).map(
                        (o) => o.value,
                      );
                      updateAction("notify_users", { profile_ids: selected });
                    }}
                    className="w-full px-2.5 py-1.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 min-h-[100px]"
                  >
                    {(profiles || []).map(
                      (p: {
                        id: string;
                        first_name?: string;
                        last_name?: string;
                        email?: string;
                      }) => (
                        <option key={p.id} value={p.id}>
                          {[p.first_name, p.last_name]
                            .filter(Boolean)
                            .join(" ") ||
                            p.email ||
                            p.id}
                        </option>
                      ),
                    )}
                  </select>
                </div>
              )}
            </div>

            {/* Enroll in Sequences */}
            <div className="border border-gray-200 rounded-lg p-4">
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-sm font-medium text-gray-900">
                  Enroll in Sequence(s)
                </h3>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    className="sr-only peer"
                    checked={
                      completionActions.enroll_in_sequences?.enabled || false
                    }
                    onChange={(e) =>
                      updateAction("enroll_in_sequences", {
                        enabled: e.target.checked,
                      })
                    }
                  />
                  <div className="w-9 h-5 bg-gray-200 rounded-full peer peer-checked:bg-blue-600 after:content-[''] after:absolute after:top-0.5 after:start-0.5 after:bg-white after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:after:translate-x-4" />
                </label>
              </div>
              {completionActions.enroll_in_sequences?.enabled && (
                <div>
                  <label className="block text-xs text-gray-600 mb-1">
                    Select Sequences (up to {SEQUENCE_ENROLL_CAP})
                  </label>
                  <select
                    multiple
                    value={
                      completionActions.enroll_in_sequences?.sequence_ids || []
                    }
                    onChange={(e) => {
                      const selected = Array.from(e.target.selectedOptions).map(
                        (o) => o.value,
                      );
                      updateAction("enroll_in_sequences", {
                        sequence_ids: selected,
                      });
                    }}
                    className="w-full px-2.5 py-1.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 min-h-[100px]"
                  >
                    {(activeSequences || []).map(
                      (s: { id: string; name: string }) => (
                        <option key={s.id} value={s.id}>
                          {s.name}
                        </option>
                      ),
                    )}
                    {/* Saved selections whose sequence is no longer active:
                        without an <option>, the browser drops them from
                        selectedOptions and the next save would silently
                        remove them. Render them so the operator can SEE and
                        deliberately keep or deselect them (they are skipped
                        at submission time either way). */}
                    {(() => {
                      const activeIds = new Set(
                        (activeSequences || []).map(
                          (s: { id: string }) => s.id,
                        ),
                      );
                      return (
                        completionActions.enroll_in_sequences?.sequence_ids ||
                        []
                      )
                        .filter((sid) => !activeIds.has(sid))
                        .map((sid) => (
                          <option key={sid} value={sid}>
                            (no longer active — skipped at submit) {sid}
                          </option>
                        ));
                    })()}
                  </select>
                  {(completionActions.enroll_in_sequences?.sequence_ids || [])
                    .length > SEQUENCE_ENROLL_CAP && (
                    <p className="mt-1 text-xs text-red-600">
                      {
                        (
                          completionActions.enroll_in_sequences?.sequence_ids ||
                          []
                        ).length
                      }{" "}
                      selected — the limit is {SEQUENCE_ENROLL_CAP}. Deselect
                      down to {SEQUENCE_ENROLL_CAP} or fewer to save.
                    </p>
                  )}
                  <p className="mt-1 text-xs text-gray-500">
                    Hold Cmd/Ctrl to select multiple (up to{" "}
                    {SEQUENCE_ENROLL_CAP}). Each submitter is enrolled in the
                    selected journey(s) when the form is submitted. Only active
                    sequences are shown; a sequence paused later is skipped at
                    submission time. Contacts already enrolled are not enrolled
                    twice.
                  </p>
                </div>
              )}
            </div>
          </div>
        </section>

        {/* Section 4: Endpoint URL (shown after first save) */}
        {savedId && (
          <section className="bg-white border border-gray-200 rounded-xl p-5">
            <h2 className="text-base font-semibold text-gray-900 mb-4">
              Endpoint URL
            </h2>
            <div className="space-y-3">
              <div>
                <label className="block text-xs font-medium text-gray-500 mb-1 uppercase tracking-wide">
                  Production URL
                </label>
                <div className="flex items-center gap-2">
                  <code className="flex-1 px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg text-xs font-mono text-gray-800 break-all">
                    {getFormHandlerEndpointUrl(savedId)}
                  </code>
                  <button
                    onClick={() => copyUrl("primary")}
                    className="shrink-0 p-2 text-gray-400 hover:text-gray-600"
                    title="Copy"
                  >
                    <ClipboardDocumentIcon className="w-4 h-4" />
                  </button>
                  {copiedUrl === "primary" && (
                    <span className="text-xs text-green-600 shrink-0">
                      Copied!
                    </span>
                  )}
                </div>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-500 mb-1 uppercase tracking-wide">
                  QA / Testing URL (direct Supabase)
                </label>
                <div className="flex items-center gap-2">
                  <code className="flex-1 px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg text-xs font-mono text-gray-800 break-all">
                    {getFormHandlerDirectUrl(savedId)}
                  </code>
                  <button
                    onClick={() => copyUrl("direct")}
                    className="shrink-0 p-2 text-gray-400 hover:text-gray-600"
                    title="Copy"
                  >
                    <ClipboardDocumentIcon className="w-4 h-4" />
                  </button>
                  {copiedUrl === "direct" && (
                    <span className="text-xs text-green-600 shrink-0">
                      Copied!
                    </span>
                  )}
                </div>
              </div>
            </div>
          </section>
        )}
      </div>

      {/* Save button */}
      <div className="mt-6 flex items-center justify-end gap-3">
        <button
          onClick={() => navigate("/form-handlers")}
          className="px-4 py-2 text-sm text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50"
        >
          Cancel
        </button>
        <button
          onClick={handleSave}
          disabled={saving}
          className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-50"
        >
          {saving ? "Saving..." : isNew ? "Create Handler" : "Save Changes"}
        </button>
      </div>
    </div>
  );
}
